import * as R from 'ramda'
import {
  useMemo,
  useCallback,
  memo,
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
} from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Skeleton, styled } from '@mui/material'
import { VariableSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { Message } from '@weblite/ts-action/message'
import { DateSeparator } from '../date-separator'
import { formatMessages, MediaListRow } from './media-list.helper'
import { MessageSelector } from '../message-bodies/message-selector'
import { SharedMediaSelectionContext } from '../../shared-media'

const BASE_GRID_GAP = 4
const SKELETON_HEIGHT = 70
const DATE_SEPARATOR_HEIGHT = 36
const SINGLE_ITEM_HEIGHT = 70

// NOTE: Heights for link panel
const ONE_LINE_LINK_ITEM_HEIGHT = 24
// margin-top + title + gap + description + margin-bottom + divider
const LINK_BOX_HEIGHT = 8 + 20 + 4 + 20 + 8 + 1

const ItemContainer = styled('div')`
  margin-left: ${BASE_GRID_GAP}px;
  &:last-child {
    margin-left: 0;
  }
`

const ListContainer = styled('div')`
  width: 100%;
  & div {
    direction: inherit !important;
    text-align: inherit !important;
  }
`

const MultiItemRowWrapper = styled('div')`
  display: flex;
  flex-direction: row;
`

interface Props {
  height: number
  items: Message[]
  columnCount?: number
  loading?: boolean
  hasMoreItemToLoad: boolean
  overscanCount?: number
  loadMore: () => Promise<void>
  sharedMediaRef: any
  setSelection: (pk: number, selected: boolean) => void
  isOnLinkTab: boolean
}

export function MediaList({
  height,
  items = [],
  columnCount = 1,
  loading = false,
  overscanCount = 3,
  loadMore,
  hasMoreItemToLoad,
  sharedMediaRef,
  setSelection,
  isOnLinkTab,
}: Props) {
  const [multiItemHeight, setMultiItemHeight] = useState(null)

  const formattedItems = useMemo(() => formatMessages(items, columnCount), [
    items,
    columnCount,
  ])

  const countOfDateSeparatorsSoFarMap = useMemo(
    () =>
      R.scan(
        (countSoFar, row) =>
          !Array.isArray(row) ? countSoFar + 1 : countSoFar,
        0,
      )(formattedItems),
    [formattedItems],
  )

  const rowCount = formattedItems.length

  const renderRow = useCallback(
    memo(
      ({
        data,
        index: rowIndex,
        style,
      }: {
        data: MediaListRow[]
        index: number
        style: any
      }) => {
        const selection: Record<string, boolean> = useContext(
          SharedMediaSelectionContext,
        )
        const selectionCount = Object.values(selection).filter(Boolean).length
        const onItemSelectedChange = useCallback(
          (selected: boolean) => (iid: number) => setSelection(iid, selected),
          [],
        )

        const dateSeparatorsSoFar = countOfDateSeparatorsSoFarMap[rowIndex]
        const gridGap = columnCount === 1 ? 0 : BASE_GRID_GAP

        if (rowIndex >= rowCount)
          return (
            <div key={rowIndex} style={style}>
              <Skeleton width="100%" height={50} />
            </div>
          )

        const row = data[rowIndex]
        if (!Array.isArray(row))
          // row is date-separator
          return (
            <div
              key={rowIndex}
              style={{
                ...style,
                width: '100%',
                paddingTop: `${Math.sign(dateSeparatorsSoFar) * gridGap * 2}px`,
              }}
            >
              <DateSeparator date={row.date} />
            </div>
          )

        if (columnCount === 1) {
          const [itemData] = data[rowIndex] as Message[]
          const { iid } = itemData
          const hideDivider =
            rowIndex === rowCount - 1 || !Array.isArray(data[rowIndex + 1])

          return (
            <div
              key={rowIndex}
              style={{
                ...style,
              }}
            >
              <MessageSelector
                isOnLinkTab={isOnLinkTab}
                message={itemData}
                selected={!!selection[iid]}
                selectOnTap={selectionCount > 0}
                hideDivider={hideDivider}
                onSelectedChange={(selected: boolean) =>
                  onItemSelectedChange(selected)(iid)
                }
              />
            </div>
          )
        }

        // row is an array of items
        return (
          <MultiItemRowWrapper
            key={rowIndex}
            style={{ ...style, paddingTop: `${BASE_GRID_GAP}px` }}
          >
            {row.map(item => {
              const { iid } = item
              return (
                <ItemContainer
                  key={item.id}
                  style={{
                    width: `calc(${100 / columnCount}% - ${((columnCount - 1) *
                      gridGap) /
                      columnCount}px)`,
                  }}
                >
                  <MessageSelector
                    message={item}
                    selected={!!selection[iid]}
                    selectOnTap={selectionCount > 0}
                    onSelectedChange={(selected: boolean) =>
                      onItemSelectedChange(selected)(iid)
                    }
                  />
                </ItemContainer>
              )
            })}
          </MultiItemRowWrapper>
        )
      },
    ),
    [items.length, rowCount, columnCount, loading],
  )

  const isRowLoaded = (rowIndex: number) => rowIndex < rowCount

  const rowCountWithLoading =
    loading || !hasMoreItemToLoad ? rowCount : rowCount + 1

  if (
    sharedMediaRef?.current?.clientWidth &&
    sharedMediaRef?.current?.clientWidth !== multiItemHeight
  )
    setMultiItemHeight(sharedMediaRef?.current?.clientWidth)

  const listRef = useRef<VariableSizeList<MediaListRow[]>>()
  //  Resetting react window styles cache after load more, This is needed because height of skeleton is changed to height of row/date-separator and re-calculation is needed.
  //  https://github.com/bvaughn/react-window/issues/199#issuecomment-479957451
  useLayoutEffect(() => {
    listRef.current?.resetAfterIndex(0, false)
  }, [formattedItems])

  const getRowHeight = useCallback(
    (rowIndex: number) => {
      //  is skeleton
      if (rowIndex >= formattedItems.length) return SKELETON_HEIGHT

      const dateSeparatorSoFar = countOfDateSeparatorsSoFarMap[rowIndex]
      const gridGap = columnCount === 1 ? 0 : BASE_GRID_GAP
      const row = formattedItems[rowIndex]

      // is date-separator
      if (!Array.isArray(row))
        return (
          DATE_SEPARATOR_HEIGHT + Math.sign(dateSeparatorSoFar) * gridGap * 2
        )

      //  is one column row
      if (columnCount === 1) {
        if (isOnLinkTab) {
          return (
            LINK_BOX_HEIGHT +
            // @ts-ignore
            formattedItems[rowIndex][0]?.links?.length *
              ONE_LINE_LINK_ITEM_HEIGHT
          )
        }

        return SINGLE_ITEM_HEIGHT + 1 // + DIVIDER_HEIGHT
      }

      // is array of items
      return (
        ((multiItemHeight || window.innerWidth) -
          (columnCount - 1) * BASE_GRID_GAP) /
          columnCount +
        BASE_GRID_GAP
      )
    },
    [formattedItems, columnCount, multiItemHeight],
  )

  return (
    <ListContainer
      sx={{
        height: `${height}px`,
      }}
    >
      <AutoSizer>
        {({ width, height: listHeight }) => (
          <>
            <InfiniteLoader
              itemCount={rowCountWithLoading}
              // ref={infiniteLoaderRef}
              isItemLoaded={isRowLoaded}
              loadMoreItems={() => {
                loadMore()
              }}
            >
              {({ onItemsRendered, ref }) => (
                <VariableSizeList
                  ref={list => {
                    listRef.current = list || undefined
                    ref(list)
                  }}
                  onItemsRendered={onItemsRendered}
                  itemData={formattedItems}
                  height={listHeight}
                  width={width}
                  itemSize={getRowHeight}
                  overscanCount={overscanCount}
                  itemCount={rowCountWithLoading}
                >
                  {renderRow}
                </VariableSizeList>
              )}
            </InfiniteLoader>
          </>
        )}
      </AutoSizer>
    </ListContainer>
  )
}
